import React from "react";
import styled from "styled-components";
import { Input, InputProps } from "@mui/material";

import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";

import { DataFormControl } from "../DataFormControl";
import { IssueData } from "../../Utilities/ControlRenderContext";
import { StyledFormHelperText } from "./CommonStyled";

import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { StringDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { StyledButton } from "../../../CommonUI/BuildlingBlocks/StyledButton";
import { EditGrayIcon } from "../../../CommonUI/Icon/EditGrayIcon";
import { CloseRedIcon } from "../../../CommonUI/Icon/CloseRedIcon";
import { ApplyIcon } from "../../../CommonUI/Icon/ApplyIcon";
import { UILayer } from "../../../CommonUI/BuildlingBlocks/UILayer";
import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UIToggleButtons } from "../../../CommonUI/BuildlingBlocks/UIToggleButtons";
import { UIButton } from "../../../CommonUI/BuildlingBlocks/UIButton";
import { Box } from "@mui/material";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";

export enum ListEntryType {
    LIST = "LIST",
    TEXT = "TEXT",
}

class XListEntryUI extends React.Component<{ editMe: ListEntryFormControl }> {
    render() {
        return <UISpacedBoxContent>{this.props.editMe.renderBaseDataControlEditor()}</UISpacedBoxContent>;
    }
}

export class ListEntryFormControl extends DataFormControl {
    readonly __type: string = "List Entry";
    mode: ListEntryType = ListEntryType.LIST;

    render() {
        const issueData: IssueData | null = this.getFormContext()!.getControlContext(this)!.getRuntimeIssueData();
        const issueText: string = issueData?.text || "";

        return (
            <XListEntryRender data-testid={this.valueName}>
                <UILabel variant={"form_label"}>{this.label}</UILabel>
                <ObserveState
                    listenTo={this}
                    properties={["mode"]}
                    control={() => {
                        return (
                            <Box
                                border={"1px solid " + UIStyleConstants.GRAY_LIGHTER_COLOR}
                                borderRadius="4pxpx"
                                padding={"5px"}
                            >
                                <Box
                                    display={"flex"}
                                    flexDirection={"row"}
                                    justifyContent={"center"}
                                >
                                    <UIToggleButtons
                                        value={this.mode}
                                        onChange={(value: any) => {
                                            this.mode = value;
                                            StateManager.propertyChanged(this, "mode");
                                        }}
                                        options={[ListEntryType.LIST, ListEntryType.TEXT]}
                                    />
                                </Box>
                                {this.mode === ListEntryType.LIST ? (
                                    <TextList
                                        items={this.getItems()}
                                        updateCallback={(newItems: string[]) => {
                                            if (this.valueName) {
                                                this.setFormDataValue(this.valueName, newItems);
                                            }
                                        }}
                                    />
                                ) : (
                                    <MultilineText
                                        items={this.getItems()}
                                        updateCallback={(newItems: string[]) => {
                                            if (this.valueName) {
                                                this.setFormDataValue(this.valueName, newItems);
                                            }
                                        }}
                                    />
                                )}
                            </Box>
                        );
                    }}
                />

                {issueText && (
                    <StyledFormHelperText error={Boolean(issueText)}>{issueText ? issueText : ""}</StyledFormHelperText>
                )}
            </XListEntryRender>
        );
    }

    renderEditUI(): JSX.Element | null {
        return <XListEntryUI editMe={this} />;
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        if (!this.valueName) return;

        let def = new StringDataDefinition();
        def.list = true;
        def.name = this.valueName;
        return def;
    }

    getItems(): string[] {
        if (this.valueName) {
            return this.getFormDataValue(this.valueName) || [];
        }
        return [];
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(this, retData, "__type", "mode");

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(this, data, "__type", "mode");
    }
}

interface ListEntryEditorProps {
    items: string[];
    updateCallback: (newItems: string[]) => void;
}

const MultilineText = (props: ListEntryEditorProps) => {
    const [items, setItems] = React.useState<string[]>(() => {
        if (props.items.length) {
            return props.items;
        } else return [""];
    });
    const [isEdit, setIsEdit] = React.useState<boolean>(false);

    const [currentIdx, setCurrentIdx] = React.useState<number>(0);

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === 13) {
            if (currentIdx === items.length - 1) {
                const tempItems = [...items, ""];
                setItems([...tempItems]);
            }
            setCurrentIdx(currentIdx + 1);
        } else if (event.keyCode === 8) {
            if (items[currentIdx] === "") {
                const tempItems = [...items];
                tempItems.splice(currentIdx, 1);

                setItems([...tempItems]);
                setCurrentIdx(currentIdx - 1);
            }
        }
    };

    return (
        <>
            <MultipleTextRender
                isEdit={isEdit}
                data-testid="text-mode-render"
            >
                {items.map((item, idx) => (
                    <React.Fragment key={idx}>
                        <IndexRender>{idx + 1}.</IndexRender>
                        <MultilineTextItemInput
                            value={item}
                            readOnly={!isEdit}
                            disableUnderline
                            onFocus={() => {
                                setCurrentIdx(idx);
                            }}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                const tempItems = [...items];
                                tempItems[idx] = event.target.value as string;
                                setItems([...tempItems]);
                            }}
                            onKeyDown={onKeyDown}
                            autoFocus={idx === currentIdx}
                            data-testid={`textmode-input-${idx}`}
                        />
                    </React.Fragment>
                ))}
            </MultipleTextRender>
            <UIButton
                variant="contained"
                sx={{ marginTop: 12 }}
                data-testid="textmode-switch"
                onClick={() => {
                    if (isEdit) {
                        props.updateCallback(items);
                        setIsEdit(false);
                    } else {
                        setIsEdit(true);
                    }
                }}
            >
                {isEdit ? "Save" : "Edit"}
            </UIButton>
        </>
    );
};

const TextList = (props: ListEntryEditorProps) => {
    const [items, setItems] = React.useState<string[]>(props.items);
    const [newItems, setNewItems] = React.useState<string[]>([]);

    const [currentIdx, setCurrentIdx] = React.useState<number>(-1);

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === 13) {
            if (currentIdx === items.length - 1) {
                const tempItems = [...items, ""];
                setItems([...tempItems]);
            }
            setCurrentIdx(currentIdx + 1);
        }
    };

    const onPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
        let pastedItems = event.clipboardData.getData("Text").split("\n");

        if (pastedItems.length > 1) {
            setNewItems(pastedItems);
        }
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        let tempItems = [...items];

        let deleteItems = tempItems.splice(result.source.index, 1);

        tempItems.splice(result.destination.index, 0, deleteItems[0]!);

        setItems([...tempItems]);
        props.updateCallback([...tempItems]);
    };

    return (
        <>
            <TextListRender
                onPaste={onPaste}
                data-testid="list-mode-render"
            >
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="droppable">
                        {(provided) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                            >
                                {(!items || items.length === 0) && <UILabel variant={"form_label"}>No Items</UILabel>}
                                {items.map((item, idx) => (
                                    <Draggable
                                        key={idx}
                                        draggableId={`item_${idx}`}
                                        index={idx}
                                        isDragDisabled={idx === currentIdx}
                                    >
                                        {(provided) => (
                                            <TextListItem
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                            >
                                                <IndexRender>{idx + 1}.</IndexRender>
                                                <TextListItemInput
                                                    edit={currentIdx === idx}
                                                    value={item}
                                                    readOnly={currentIdx !== idx}
                                                    disableUnderline
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                        const tempItems = [...items];
                                                        tempItems[idx] = event.target.value as string;
                                                        setItems([...tempItems]);
                                                        props.updateCallback([...tempItems]);
                                                    }}
                                                    onKeyDown={onKeyDown}
                                                    data-testid={`listmode-input-${idx}`}
                                                />

                                                <ActionButton
                                                    data-testid={`listmode-editswitch-${idx}`}
                                                    onClick={() => {
                                                        if (idx === currentIdx) {
                                                            setCurrentIdx(-1);
                                                            props.updateCallback(items);
                                                        } else {
                                                            setCurrentIdx(idx);
                                                        }
                                                    }}
                                                >
                                                    {idx === currentIdx ? <ApplyIcon /> : <EditGrayIcon />}
                                                </ActionButton>

                                                <ActionButton
                                                    data-testid={`listmode-delete-${idx}`}
                                                    onClick={() => {
                                                        const tempItems = [...items];
                                                        tempItems.splice(idx, 1);
                                                        setItems([...tempItems]);
                                                        props.updateCallback([...tempItems]);
                                                    }}
                                                >
                                                    <CloseRedIcon />
                                                </ActionButton>
                                            </TextListItem>
                                        )}
                                    </Draggable>
                                ))}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </TextListRender>
            <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"end"}
            >
                <UIButton
                    variant="contained"
                    onClick={() => {
                        const tempItems = [...items, ""];
                        setItems([...tempItems]);
                        props.updateCallback([...tempItems]);
                    }}
                >
                    Add
                </UIButton>
            </Box>

            <UILayer
                open={newItems.length > 1}
                padding="36px 32px 32px"
                renderUI={() => {
                    return (
                        <>
                            <LayerDescription>Insert data as one row or multiple rows?</LayerDescription>
                            <LayerActions>
                                <StyledButton
                                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                    variant="outlined"
                                    onClick={() => {
                                        setItems([...items, newItems.join("\n")]);
                                        props.updateCallback([...items, newItems.join("\n")]);
                                        setNewItems([]);
                                    }}
                                >
                                    One
                                </StyledButton>
                                <StyledButton
                                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                    variant="outlined"
                                    onClick={() => {
                                        setItems([...items, ...newItems]);
                                        props.updateCallback([...items, ...newItems]);
                                        setNewItems([]);
                                    }}
                                >
                                    Multiple
                                </StyledButton>
                            </LayerActions>
                        </>
                    );
                }}
                showCloseButton
                onClose={() => {
                    setNewItems([]);
                }}
            />
        </>
    );
};

const TextListRender = styled.div`
    display: flex;
    flex-direction: column;

    padding: 16px 8px;

    border: 1px solid transparent;

    column-gap: 4px;
    row-gap: 0;
`;

const TextListItem = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;

    padding: 0 2px;

    > * {
        margin: 0 2px !important;
    }
`;

const MultipleTextRender = styled.div<{ isEdit?: boolean }>`
    display: grid;
    grid-template-columns: max-content max-content;

    padding: 16px 8px;

    border: 1px solid transparent;

    column-gap: 8px;
    row-gap: 12px;

    ${(props) =>
        props.isEdit
            ? `
                border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
                border-radius: 4px;
            `
            : ``}
`;

const IndexRender = styled.div`
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: left;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
    display: flex;
    align-items: center;
    justify-content: center;
`;

interface TextListItemInputProps extends InputProps {
    edit: boolean;
}

const TextListItemInput = ({ edit, ...props }: TextListItemInputProps) => {
    if (edit) return <StyledEditTextListItemInput {...props} />;
    return <StyledTextListItemInput {...props} />;
};

const StyledEditTextListItemInput = styled(Input)`
    && {
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 17px;
        letter-spacing: 0px;
        text-align: left;
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        flex: 1;

        .MuiInputBase-input {
            padding: 6px 12px;
            border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
            border-radius: 4px;
        }
    }
`;

const StyledTextListItemInput = styled(Input)`
    && {
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 17px;
        letter-spacing: 0px;
        text-align: left;
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        flex: 1;
        position: relative;

        &::after {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 2;
        }

        .MuiInputBase-input {
            padding: 6px 12px;
            border: 1px solid transparent;
        }
    }
`;

const MultilineTextItemInput = styled(Input)`
    && {
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 17px;
        letter-spacing: 0px;
        text-align: left;
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

        .MuiInputBase-input {
            padding: 0;
        }
    }
`;

const ActionButton = styled.div`
    width: 16px;
    height: 16px;

    display: flex;
    align-items: center;
    justify-center: center;

    cursor: pointer;

    &:hover {
        opacity: 0.8;
    }
`;

const LayerDescription = styled.div`
    //styleName: title/h3 Inter SemiBold 16px;

    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 22px;
    letter-spacing: 0em;
    text-align: center;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
`;

const LayerActions = styled.div`
    display: flex;
    flex-direction: row;

    padding: 8px 0 0;

    > * {
        width: 50%;
        margin: 8px 6px 0 !important;
    }
`;

const XListEntryRender = styled.div`
    display: flex;
    flex-direction: column;
`;
